import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { Capacitor } from '@capacitor/core';
import { apiFetch, getToken, clearToken, isTokenExpired, track, DPP_BASE_URL, API_BASE_URL, formatDate } from '../dppApi';
import UserAssistant from './UserAssistant';
import UserInquiries from './UserInquiries';
import Pager from './Pager';
import AccountMenu from './AccountMenu';
import { usePaged } from '../hooks/usePaged';
import '../App.css';

const bloomMark = process.env.PUBLIC_URL + '/BloomFavicon.png';

// In the packaged mobile app Ask Bloom leads, above the pieces; on the web it
// stays in the sidebar where the wider layout already puts it beside them.
// The web build must be untouched, so this is gated on the native platform
// rather than on a breakpoint.
const ASSISTANT_FIRST = Capacitor.isNativePlatform();

// The category row wraps onto two rows on a phone, costing ~90px before the
// first piece. In the app it becomes a single sideways-scrolling strip; the web
// keeps the wrapping row it has today.
const SCROLL_CATEGORIES = Capacitor.isNativePlatform();


// The brand identity strip at the top of each card, rendered exactly like the
// passport's brand header (see Passport.js): the brand's header logo when it has
// one, otherwise the brand title/name in the brand's own colour and font. Keeps
// its own logo-failure state so one broken logo falls back to text on its card
// alone. `p` is a collection item from /api/user/collection.
const CardBrandHeader = ({ p }) => {
    const [logoFailed, setLogoFailed] = useState(false);
    const label = p.brand_title || p.brand_name;
    if (!label && !p.brand_header_logo_url) return null;

    const showLogo = p.brand_header_logo_url && !logoFailed;
    const logo = (
        <img
            className="collection-card-brandhead-logo"
            src={p.brand_header_logo_url}
            alt={label || 'Brand'}
            onError={() => setLogoFailed(true)}
        />
    );

    return (
        <div className="collection-card-brandhead">
            {showLogo ? (
                p.brand_header_logo_bg_color ? (
                    <div
                        className="collection-card-brandhead-logo-wrap"
                        style={{ backgroundColor: p.brand_header_logo_bg_color }}
                    >{logo}</div>
                ) : logo
            ) : (
                <span style={{ color: p.brand_text_color || p.brand_color || undefined,
                               fontFamily: p.brand_font ? `'${p.brand_font}', sans-serif` : undefined }}>
                    {label}
                </span>
            )}
        </div>
    );
};

// Jewellery-type buckets for the category menu. Order matters: earrings before
// rings ("earring" contains "ring"); pendants/chains before necklaces. Anything
// unmatched is "General" (the catch-all).
const COLLECTION_CATEGORIES = [
    { key: 'earrings', label: 'Earrings', re: /\b(?:ear[\s-]?rings?|earrings?|studs?)\b/ },
    { key: 'bracelets', label: 'Bracelets & Bangles', re: /\b(?:bracelets?|bangles?|kada|cuff)\b/ },
    { key: 'chains', label: 'Chains & Pendants', re: /\b(?:chains?|pendants?)\b/ },
    { key: 'necklaces', label: 'Necklaces', re: /\b(?:necklaces?|necklet|choker|riviere|collar)\b/ },
    { key: 'rings', label: 'Rings', re: /\brings?\b/ },
];
const GENERAL_CATEGORY = { key: 'general', label: 'General' };
// Menu display order — independent of the classification precedence above.
const COLLECTION_DISPLAY_ORDER = ['earrings', 'necklaces', 'bracelets', 'chains', 'rings', 'general'];

const classifyPiece = (name) => {
    const n = (name || '').toLowerCase();
    const hit = COLLECTION_CATEGORIES.find(c => c.re.test(n));
    return hit ? hit.key : 'general';
};

// Friendly labels for a maintenance request's lifecycle status (see
// models.MaintenanceRequest / routes/maintenance.py).
const MAINTENANCE_STATUS_LABELS = {
    pending: 'Pending',
    accepted: 'Accepted',
    in_service: 'In service',
    ready: 'Ready for pickup',
};

// A user's collection: every product they currently or previously owned, split
// into Owned and Sold. For sold items a user can still view the documents the
// backend allows them (their chain up to the extent of their ownership, plus
// their own insurance).
const UserCollection = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const token = getToken('user');
    // Inquiry seed handed over by the passport "Contact Merchant" link, which opens
    // this page in a NEW TAB — so the context rides in the URL query (?inquire=<qr>
    // &product=&merchant=), not router state (which can't cross tabs). Read it once,
    // then strip the query so a refresh doesn't reopen the same pop-up.
    // Both passport "Contact Merchant" buttons land here: ?inquire=<qr> opens the
    // inquiry flow, ?repair=<qr> opens the maintenance (pick-up/drop-off) flow.
    // Either way Ask Bloom opens as a pop-up, seeded for this piece; the `kind`
    // discriminator tells the assistant which flow to start.
    const [inquirySeed] = useState(() => {
        const p = new URLSearchParams(location.search);
        const qr = p.get('inquire');
        return qr ? { kind: 'inquiry', qr_code_id: qr, product: p.get('product') || '', merchant: p.get('merchant') || '' } : null;
    });
    const [repairSeed] = useState(() => {
        const p = new URLSearchParams(location.search);
        const qr = p.get('repair');
        return qr ? { kind: 'maintenance', qr_code_id: qr, product: p.get('product') || '', merchant: p.get('merchant') || '' } : null;
    });
    const contactSeed = inquirySeed || repairSeed;
    useEffect(() => {
        if (contactSeed) navigate(location.pathname, { replace: true });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    // Arriving from the passport "Contact Merchant" link opens Ask Bloom as a
    // pop-up so the inquiry is front-and-centre, not buried in the side column.
    const [inquiryModalOpen, setInquiryModalOpen] = useState(!!contactSeed);
    const overlayDownRef = useRef(false);   // did the current click's mousedown start on the backdrop?
    useEffect(() => {
        if (!inquiryModalOpen) return;
        const onKey = e => { if (e.key === 'Escape') setInquiryModalOpen(false); };
        document.addEventListener('keydown', onKey);
        document.body.style.overflow = 'hidden';   // lock background scroll while open
        return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
    }, [inquiryModalOpen]);
    const [items, setItems] = useState([]);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [docPanel, setDocPanel] = useState(null);
    const [viewPanel, setViewPanel] = useState(null);
    const [activeCat, setActiveCat] = useState('all');   // category-menu tab
    const [maintenance, setMaintenance] = useState([]);  // the user's open service requests

    useEffect(() => {
        // An expired token still passes a bare `!token` check — see BrandDashboard.
        if (!token || isTokenExpired(token)) { clearToken('user'); navigate('/user'); return; }
        apiFetch('/api/user/collection', { token })
            .then(setItems)
            .catch((err) => setError(err.message))
            .finally(() => setLoading(false));
        // Pending maintenance is non-critical — if it fails, just hide the panel.
        apiFetch('/api/maintenance', { token })
            .then(setMaintenance)
            .catch(() => {});
    }, [token, navigate]);

    // Mirror the passport's brand-font loading (Passport.js): inject each brand's
    // font stylesheet so text-based card headers render in the brand's own
    // typeface. Deduped across the collection's brands — CDN fonts (Google/Typekit)
    // load directly, others via the backend font-proxy. Links are cleaned up on
    // unmount / when the collection changes.
    useEffect(() => {
        const seen = new Set();
        const links = [];
        for (const p of items) {
            const fontUrl = p.brand_font_url;
            if (!fontUrl || seen.has(fontUrl)) continue;
            seen.add(fontUrl);
            const isCdn = fontUrl.includes('fonts.googleapis.com') || fontUrl.includes('use.typekit.net');
            let href;
            if (isCdn) {
                href = fontUrl;
            } else {
                const params = new URLSearchParams({ url: fontUrl });
                if (p.brand_font) params.set('family', p.brand_font);
                href = `${API_BASE_URL}/api/font-proxy?${params}`;
            }
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = href;
            document.head.appendChild(link);
            links.push(link);
        }
        return () => { links.forEach(link => { if (document.head.contains(link)) document.head.removeChild(link); }); };
    }, [items]);

    const logout = () => { clearToken('user'); navigate('/user'); };

    const DOC_META = {
        insurance: { label: 'Upload insurance', fileLabel: 'Insurance document', issuerLabel: 'Insurer', placeholder: "e.g. Lloyd's of London" },
        appraisal: { label: 'Upload appraisal', fileLabel: 'Appraisal document', issuerLabel: 'Appraiser', placeholder: 'e.g. GIA' },
    };

    const openDoc = (productId, docType) =>
        setDocPanel({ productId, docType, file: null, issuedBy: '', issuedDate: '', busy: false, error: null, done: false });

    const closeDoc = () => setDocPanel(null);

    const openDocs = async (product) => {
        setViewPanel({ productId: product.product_id, loading: true, docs: [], error: null });
        try {
            const docs = await apiFetch(`/api/passport/${product.qr_code_id}/private-docs`, { token });
            setViewPanel({ productId: product.product_id, loading: false, docs, error: null });
        } catch (err) {
            setViewPanel({ productId: product.product_id, loading: false, docs: [], error: err.message });
        }
    };

    const closeDocs = () => setViewPanel(null);

    const uploadDoc = async () => {
        if (!docPanel.file) return;
        setDocPanel(prev => ({ ...prev, busy: true, error: null }));
        try {
            const form = new FormData();
            form.append('document', docPanel.file);
            form.append('doc_type', docPanel.docType);
            if (docPanel.issuedBy) form.append('issued_by', docPanel.issuedBy);
            if (docPanel.issuedDate) form.append('issued_date', docPanel.issuedDate);
            await apiFetch(`/api/products/${docPanel.productId}/documents/owner`, { method: 'POST', token, body: form, isForm: true });
            setDocPanel(prev => ({ ...prev, busy: false, done: true }));
        } catch (err) {
            setDocPanel(prev => ({ ...prev, busy: false, error: err.message }));
        }
    };

    const renderCard = (p) => (
        <div className="collection-card" key={p.product_id}>
            <CardBrandHeader p={p} />
            {p.image_url && p.image_url.startsWith('http')
                ? <img className="collection-card-img" src={p.image_url} alt={p.name} />
                : <div className="collection-card-img collection-card-img--empty">No image</div>}

            <div className="collection-card-body">
                <div className="collection-card-title">{p.name}</div>

                {/* Actions + QR share a footer row (as on the brand dashboard) so the
                    QR stays anchored to the buttons. It must NOT be absolutely
                    positioned to the card, or it slides down whenever a panel below
                    expands the card's height. */}
                <div className="collection-card-footer">
                    <div className="collection-card-actions">
                        <a className="collection-action" href={`${DPP_BASE_URL}/jewellery/${p.qr_code_id}`} target="_blank" rel="noreferrer">View passport</a>
                        {p.is_current_owner && Object.keys(DOC_META).map(docType => (
                            <button
                                key={docType}
                                className="collection-action"
                                onClick={() =>
                                    docPanel?.productId === p.product_id && docPanel?.docType === docType
                                        ? closeDoc()
                                        : openDoc(p.product_id, docType)
                                }
                            >
                                {docPanel?.productId === p.product_id && docPanel?.docType === docType
                                    ? 'Cancel'
                                    : DOC_META[docType].label}
                            </button>
                        ))}
                        {!p.is_current_owner && (
                            <button
                                className="collection-action"
                                onClick={() => (viewPanel?.productId === p.product_id ? closeDocs() : openDocs(p))}
                            >
                                {viewPanel?.productId === p.product_id ? 'Hide documents' : 'View my documents'}
                            </button>
                        )}
                    </div>
                    <QRCodeSVG
                        className="collection-card-qr"
                        value={`${DPP_BASE_URL}/jewellery/${p.qr_code_id}`}
                        size={64}
                        marginSize={2}
                    />
                </div>

                {docPanel?.productId === p.product_id && (
                    <div className="dpp-transfer-panel">
                        {docPanel.done ? (
                            <p className="catalogue-hint catalogue-hint--done">
                                {DOC_META[docPanel.docType]?.label.replace('Upload', '')} uploaded.
                            </p>
                        ) : (
                            <div className="catalogue-body">
                                <label className="dpp-transfer-label">{DOC_META[docPanel.docType]?.fileLabel}</label>
                                <input
                                    type="file"
                                    accept=".pdf,.jpg,.jpeg,.png"
                                    onChange={e => setDocPanel(prev => ({ ...prev, file: e.target.files[0] || null }))}
                                />
                                <label className="dpp-transfer-label">
                                    {DOC_META[docPanel.docType]?.issuerLabel} <span className="catalogue-nice">optional</span>
                                </label>
                                <input
                                    className="dpp-input"
                                    placeholder={DOC_META[docPanel.docType]?.placeholder}
                                    value={docPanel.issuedBy}
                                    onChange={e => setDocPanel(prev => ({ ...prev, issuedBy: e.target.value }))}
                                />
                                <label className="dpp-transfer-label">
                                    Date <span className="catalogue-nice">optional</span>
                                </label>
                                <input
                                    className="dpp-input"
                                    type="date"
                                    value={docPanel.issuedDate}
                                    onChange={e => setDocPanel(prev => ({ ...prev, issuedDate: e.target.value }))}
                                />
                                {docPanel.error && <p className="dpp-error">{docPanel.error}</p>}
                                <button
                                    className="catalogue-btn-primary"
                                    onClick={uploadDoc}
                                    disabled={docPanel.busy || !docPanel.file}
                                >
                                    {docPanel.busy ? 'Uploading…' : 'Upload'}
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {!p.is_current_owner && viewPanel?.productId === p.product_id && (
                    <div className="dpp-transfer-panel">
                        {viewPanel.loading && <p className="dpp-muted">Loading…</p>}
                        {viewPanel.error && <p className="dpp-error">{viewPanel.error}</p>}
                        {!viewPanel.loading && !viewPanel.error && viewPanel.docs.length === 0 && (
                            <p className="dpp-muted">No documents.</p>
                        )}
                        {viewPanel.docs.map(d => (
                            <a
                                key={d.doc_id}
                                href={d.url}
                                target="_blank"
                                rel="noreferrer"
                                style={{ fontSize: 14, color: '#555' }}
                            >
                                {d.issued_by ? `${d.doc_type} — ${d.issued_by}` : d.doc_type}
                                {d.issued_date ? ` (${formatDate(d.issued_date)})` : ''}
                            </a>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );

    // Inherited pieces (received by bequest) get their own section below, kept out
    // of the main category-tabbed collection.
    const owned = items.filter(p => p.is_current_owner && p.transfer_type !== 'inheritance');
    const inherited = items.filter(p => p.is_current_owner && p.transfer_type === 'inheritance');
    const sold = items.filter(p => !p.is_current_owner);

    // The category menu shows "All" plus each non-empty jewellery type. "All"
    // lists every owned piece in API order (ungrouped); a type tab filters to it.
    const segmented = owned.length > 0;
    const ownedGroups = {};
    owned.forEach(p => {
        const k = classifyPiece(p.name);
        (ownedGroups[k] = ownedGroups[k] || []).push(p);
    });
    const allCats = [...COLLECTION_CATEGORIES, GENERAL_CATEGORY];
    const activeCategories = COLLECTION_DISPLAY_ORDER
        .map(k => allCats.find(c => c.key === k))
        .filter(c => ownedGroups[c.key]?.length);
    const menuTabs = [{ key: 'all', label: 'All' }, ...activeCategories];
    const shownCat = menuTabs.some(t => t.key === activeCat) ? activeCat : 'all';

    // "Previously Owned" stays whole — it's a short secondary section.
    const visibleOwned = shownCat === 'all' ? owned : (ownedGroups[shownCat] || []);
    const ownedPage = usePaged(visibleOwned, { resetKey: shownCat });

    return (
        <div className="dpp-page">
            <div className="collection-topbar">
                <div className="collection-topbar-brand">
                    <img className="dpp-brand-mark" src={bloomMark} alt="Bloom" />
                    <h1 className="collection-topbar-title">My Collection</h1>
                </div>
                {/* Both account actions live behind the avatar. Play requires an
                    in-app route to deletion, not only a web one. */}
                <AccountMenu
                    name={items[0]?.owner_name}
                    onLogout={logout}
                    onSettings={() => navigate('/user/profile')}
                />
            </div>

            {ASSISTANT_FIRST && (
                <div className="dpp-assistant-lead">
                    <UserAssistant />
                </div>
            )}

            <div className="dpp-dashboard-cols">
                <div className="dpp-dashboard-main">
                    <div className="dpp-products-box">
                        {loading && <p className="collection-status collection-status--flush">Loading…</p>}
                        {error && <p className="dpp-error">{error}</p>}
                        {!loading && !error && items.length === 0 && (
                            <p className="collection-status collection-status--flush">You don't own any products yet.</p>
                        )}

                        {segmented && (
                            <>
                                <div className={`catalogue-toggle collection-cat-menu${SCROLL_CATEGORIES ? ' collection-cat-menu--scroll' : ''}`}>
                                    {menuTabs.map(t => (
                                        <button
                                            key={t.key}
                                            className={`catalogue-tab${shownCat === t.key ? ' is-active' : ''}`}
                                            onClick={() => { setActiveCat(t.key); track('user', `collection_category:${t.key}`); }}
                                        >
                                            {t.label}
                                        </button>
                                    ))}
                                </div>

                                {/* "All" shows every piece in API order, ungrouped; a type tab filters. */}
                                <div className="collection-grid collection-grid--left">
                                    {ownedPage.items.map(renderCard)}
                                </div>

                                <Pager
                                    page={ownedPage.page}
                                    pageCount={ownedPage.pageCount}
                                    onPage={ownedPage.setPage}
                                    total={ownedPage.total}
                                    pageSize={ownedPage.pageSize}
                                    label="pieces"
                                />
                            </>
                        )}

                        {inherited.length > 0 && (
                            <>
                                {/* Bequeathed pieces the owner received — same header treatment
                                    as the tabs/Previously Owned so it reads as one system. */}
                                <div className="catalogue-toggle" style={{ marginTop: owned.length ? 28 : 0 }}>
                                    <h2 className="catalogue-tab is-active collection-prev-owned-tab">Inherited</h2>
                                </div>
                                <div className="collection-grid collection-grid--left">{inherited.map(renderCard)}</div>
                            </>
                        )}

                        {sold.length > 0 && (
                            <>
                                {/* Same treatment as the "All" category tab above, so both
                                    section headers read as one system. */}
                                <div className="catalogue-toggle" style={{ marginTop: (owned.length || inherited.length) ? 28 : 0 }}>
                                    <h2 className="catalogue-tab is-active collection-prev-owned-tab">Previously Owned</h2>
                                </div>
                                <div className="collection-grid collection-grid--left">{sold.map(renderCard)}</div>
                            </>
                        )}
                    </div>
                </div>

                <aside className="dpp-dashboard-side">
                    {!ASSISTANT_FIRST && <UserAssistant />}
                    <UserInquiries />
                    {maintenance.length > 0 && (
                        <div className="dpp-card insights-card assistant-card maintenance-user-panel">
                            <div className="insights-card-title">Pending maintenance</div>
                            <div className="maintenance-user-list">
                                {maintenance.map(m => (
                                    <div key={m.request_id} className="maintenance-user-item">
                                        {m.image
                                            ? <img className="maintenance-user-thumb" src={m.image} alt={m.product} />
                                            : <div className="maintenance-user-thumb maintenance-user-thumb--empty" />}
                                        <div className="maintenance-user-body">
                                            <div className="maintenance-user-name">{m.product}</div>
                                            {m.issue && <div className="maintenance-user-issue">{m.issue}</div>}
                                            <div className="maintenance-user-meta">
                                                <span className="maintenance-user-status">{MAINTENANCE_STATUS_LABELS[m.status] || m.status}</span>
                                                {m.created_at && <span className="maintenance-user-date">{formatDate(m.created_at)}</span>}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </aside>
            </div>

            {inquiryModalOpen && contactSeed && (
                // Close only on a genuine backdrop click — one that BOTH starts and
                // ends on the overlay itself. Without the mousedown-origin check, a
                // text selection dragged out of the textarea and released on the
                // backdrop registers as an overlay click and closes the modal.
                <div
                    className="inquiry-modal-overlay"
                    onMouseDown={e => { overlayDownRef.current = e.target === e.currentTarget; }}
                    onClick={e => { if (overlayDownRef.current && e.target === e.currentTarget) setInquiryModalOpen(false); }}
                >
                    <div className="inquiry-modal">
                        <button className="inquiry-modal-close" onClick={() => setInquiryModalOpen(false)} aria-label="Close">×</button>
                        <UserAssistant seed={contactSeed} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserCollection;
